import debug from "debug";
import * as THREE from "three";
const log = debug("app:zoomall");

/**
 * The `ZoomAll` class is responsible for automatically adjusting the camera zoom and position
 * to fit all relevant objects within the scene.
 * @category Experience
 * @subcategory World
 */
class ZoomAll {
  /**
   * Creates an instance of `ZoomAll`.
   * @param {Object} experience - The main experience instance containing scene, camera, controls, and event hub.
   */
  constructor(experience) {
    this.experience = experience;
    this.camera = this.experience.camera.instance; // Ensure this is an OrthographicCamera
    this.scene = this.experience.scene;
    this.controls = this.experience.camera.controls;
    this.eventHub = this.experience.eventHub;
    this.sizes = this.experience.sizes;

    this.setupEventListeners();
  }

  cleanup() {
    try {
        // Remove event listener for zooming
        if (this.eventHub) {
            this.eventHub.off("zoomAll", this.zoomToFitAll);
        }

        // Clear object references
        this.camera = null;
        this.scene = null;
        this.controls = null;
        this.eventHub = null;
        this.renderer = null;
        this.sizes = null;
        this.experience = null;

        console.log("ZoomAll cleanup completed.");
    } catch (error) {
        console.error("Error during ZoomAll cleanup:", error);
    }
}

  /**
   * Sets up event listeners for handling zoom requests.
   */
  setupEventListeners() {
    this.eventHub.on("zoomAll", this.zoomToFitAll.bind(this));
  }

  /**
   * Adjusts the camera to fit all relevant objects within the scene.
   */
  zoomToFitAll() {
    log("new request to zoom all");
    this.eventHub.emit("requestSizes");
    if (!this.scene) {
        console.error("Scene is undefined");
        return;
    }

    const validObjects = this.getValidObjects();
    if (validObjects.size === 0) {
        console.error("No valid objects in the scene");
        return;
    }

    const { box, size, center } = this.calculateBoundingBox(validObjects);

    if (size.length() === 0) {
        console.error("Bounding box size is zero");
        return;
    }

    // Set the camera position to the center of the bounding box
    this.camera.position.set(center.x, this.camera.position.y, center.z);

    // Adjust camera orientation based on y position
    if (this.camera.position.y > 0) {
        this.camera.lookAt(new THREE.Vector3(center.x, center.y - Math.abs(this.camera.position.y), center.z));
    } else {
        this.camera.lookAt(new THREE.Vector3(center.x, center.y + Math.abs(this.camera.position.y), center.z));
    }

    // Calculate the current frustum dimensions
    const frustumWidth = this.camera.right - this.camera.left;
    const frustumHeight = this.camera.top - this.camera.bottom;

    // Calculate the required zoom level to fit the bounding box within 60% of the frustum size
    const zoomX = frustumWidth / size.x;
    const zoomY = frustumHeight / size.z;
    const zoomFactor = Math.min(zoomX, zoomY) * 0.5;

    this.camera.zoom = zoomFactor;
    this.camera.updateProjectionMatrix();

    log("Zoom factor set to:", this.camera.zoom);

    this.updateControlsTarget(center);

    // ✅ Trigger render after zoom and position changes
    this.eventHub.emit("needsRender", "true");
}


  /**
   * Retrieves all valid objects in the scene that should be considered for zooming.
   * @returns {Set<THREE.Object3D>} A set of valid objects.
   */
  getValidObjects() {
    const validObjects = new Set();
    log("scena", this.scene.children);
    this.scene.traverse((obj) => {
      if (obj.type === "Group" && obj.name === "XMO") {
        validObjects.add(obj);
      }
    });
    return validObjects;
  }

  /**
   * Calculates the bounding box, size, and center of the valid objects in the scene.
   * @param {Set<THREE.Object3D>} validObjects - The set of objects to compute the bounding box for.
   * @returns {Object} An object containing the bounding box, size, and center.
   */
  calculateBoundingBox(validObjects) {
    const box = new THREE.Box3();
    validObjects.forEach((obj) => box.expandByObject(obj));

    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());

    return { box, size, center };
  }

  /**
   * Updates the camera controls target to focus on the center of the bounding box.
   * @param {THREE.Vector3} center - The center point to focus on.
   */
  updateControlsTarget(center) {
    if (this.controls) {
        this.controls.target.copy(new THREE.Vector3(center.x, center.y, center.z));
        this.controls.update();

        // ✅ Ensure render after controls change
        this.eventHub.emit("needsRender", "true");
    }
}

}

export default ZoomAll;
